<template>
  <div v-if="mode === 'week'" class="timeslot-picker__week">
    <div class="timeslot-picker__date-list">
      <template
        v-for="(day, key) in filteredWeekDates"
      >
        <div
          v-if="day.available"
          :key="key"
          :data-date="day.date.format('YYYY-MM-DD')"
          :data-selected="date === day.date.format('YYYY-MM-DD') ? 'true' : 'false'"
          class="timeslot-date__row"
        >
          <div class="timeslot-date">
            {{ day.date.format('dddd Do MMM') }}
          </div>
          <div
            :aria-required="date === day.date.format('YYYY-MM-DD') ? 'true' : 'false'"
            role="radiogroup"
            class="timeslot-picker__time-list"
          >
            <button
              v-for="{ key, label, available } in day.slots"
              :key="key"
              :data-time="key"
              :data-selected="date === day.date.format('YYYY-MM-DD') && time === key ? 'true' : 'false'"
              :disabled="!available"
              :class="{
                'timeslot-time': true,
                'timeslot-time__primary': key === 'ANY',
                'timeslot-time__disabled': !available
              }"
              type="button"
              role="radio"
              @click.prevent="available ? setDateTime(day.date, key) : null"
              @keydown.enter.prevent="available ? setDateTime(day.date, key) : null"
            >
              <span>{{ key === 'ANY' ? `Anytime (${label})` : label }}</span>
            </button>
          </div>
        </div>
      </template>

      <div
        v-if="pickerConfig && futureDate"
        :data-date="futureDate.format('YYYY-MM-DD')"
        :data-selected="date === futureDate.format('YYYY-MM-DD') ? 'true' : 'false'"
      >
        <div class="timeslot-date">
          {{ futureDate.format('dddd Do MMM') }}
        </div>
        <div
          :aria-required="date === futureDate.format('YYYY-MM-DD') ? 'true' : 'false'"
          role="radiogroup"
          class="timeslot-picker__time-list"
        >
          <button
            v-for="{ key, label, available } in times"
            :key="key"
            :data-time="key"
            :data-selected="date === futureDate.format('YYYY-MM-DD') && time === key ? 'true' : 'false'"
            :disabled="!available"
            :class="{
              'timeslot-time': true,
              'timeslot-time__primary': key === 'ANY',
              'timeslot-time__disabled': !available
            }"
            type="button"
            role="radio"
            @click.prevent="available ? setDateTime(futureDate, key) : null"
            @keydown.enter.prevent="available ? setDateTime(futureDate, key) : null"
          >
            <span>{{ key === 'ANY' ? `Any Time (${label})` : label }}</span>
          </button>
        </div>
      </div>
    </div>
    <v-btn
      v-if="!previewMoreWeeks"
      color="primary"
      small
      dense
      class="mr-2 mt-4"
      @click="previewMoreWeeks = true"
    >
      Load more days
    </v-btn>
    <v-dialog
      v-if="pickerConfig"
      v-model="pickerModal"
      persistent
      width="290px"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          v-if="pickerConfig"
          color="primary"
          text
          small
          dense
          outlined
          v-on="on"
          class="mt-4"
        >
          Show later dates?
        </v-btn>
      </template>
      <v-date-picker
        v-model="pickerDate"
        :allowed-dates="pickerConfig.all"
        :min="pickerConfig.min"
        scrollable
      >
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="pickerModal = false">Cancel</v-btn>
        <v-btn text color="primary" @click="setDate(pickerDate); pickerModal = false">OK</v-btn>
      </v-date-picker>
    </v-dialog>
  </div>
  <div v-else class="timeslot-picker__days">
    <div
      :class="{
        'columns-1': !hideTimes,
        'columns-2': hideTimes
      }"
    >
      <div :aria-label="`${type} Date`" role="radiogroup" aria-required="true" class="timeslot-picker__date-list">
        <button
          v-for="(day, key) in dates"
          :key="key"
          :data-date="day.date.format('YYYY-MM-DD')"
          :data-selected="date === day.date.format('YYYY-MM-DD') ? 'true' : 'false'"
          role="radio"
          type="button"
          class="timeslot-date"
          @click.prevent="setDate(day.date)"
          @keydown.enter.prevent="setDate(day.date)"
        >
          {{ day.date.format('dddd Do MMM') }}
        </button>
        <button
          v-if="pickerConfig && futureDate"
          :data-date="futureDate.format('YYYY-MM-DD')"
          :data-selected="date === futureDate.format('YYYY-MM-DD') ? 'true' : 'false'"
          role="radio"
          type="button"
          class="timeslot-date"
          @click.prevent="setDate(futureDate)"
          @keydown.enter.prevent="setDate(futureDate)"
        >
          {{ futureDate.format('dddd Do MMM') }}
        </button>
      </div>
      <v-dialog
        v-if="pickerConfig"
        v-model="pickerModal"
        persistent
        width="290px"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-if="pickerConfig"
            color="primary"
            text
            small
            dense
            outlined
            v-on="on"
            class="mt-4"
          >
            Show later dates?
          </v-btn>
        </template>
        <v-date-picker
          v-model="pickerDate"
          :allowed-dates="pickerConfig.all"
          :min="pickerConfig.min"
          scrollable
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="pickerModal = false">Cancel</v-btn>
          <v-btn text color="primary" @click="setDate(pickerDate); pickerModal = false">OK</v-btn>
        </v-date-picker>
      </v-dialog>
    </div>
    <div v-if="!hideTimes">
      <div :aria-label="`${type} Time`" role="radiogroup" aria-required="true" class="timeslot-picker__time-list">
        <button
          v-for="{ key, label, available } in times"
          :key="key"
          :data-time="key"
          :data-selected="time === key ? 'true' : 'false'"
          :disabled="!available"
          type="button"
          class="timeslot-time"
          @click.prevent="available ? setTime(key) : null"
          @keydown.enter.prevent="available ? setTime(key) : null"
        >
          {{ key === 'ANY' ? `Any Time (${label})` : label }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import map from 'lodash.map'
import moment from 'moment'

export default {
  name: 'TimeslotPicker',

  props: {
    mode: {
      type: String,
      required: false,
      default: () => { return 'days' }
    },

    type: {
      type: String,
      required: false,
      default: () => { return 'Collection' }
    },

    date: {
      type: String,
      required: true
    },

    time: {
      type: String,
      required: true
    },

    dates: {
      type: Array,
      required: true
    },

    times: {
      type: Array,
      required: true
    },

    pickerConfig: {
      type: Object,
      required: false,
      default: () => { return null }
    },

    hideTimes: {
      type: Boolean,
      required: false,
      default: () => { return false }
    }
  },

  data () {
    return {
      showFutureDates: false,
      previewMoreWeeks: false,
      pickerModal: false,
      pickerDate: false
    }
  },

  computed: {
    futureDate () {
      if (map(this.dates, (day) => { return day.date.format('YYYY-MM-DD') }).includes(this.date)) {
        return false
      }
      return moment(this.date)
    },

    filteredWeekDates () {
      return this.dates?.slice(0, this.previewMoreWeeks ? this.dates.length : this.dates.length / 2) || []
    }
  },

  watch: {
    pickerModal (newVal, oldVal) {
      if (newVal) {
        this.pickerDate = this.date
      }
    }
  },

  methods: {
    setDate (value) {
      this.$emit('update:date', value instanceof moment ? value.format('YYYY-MM-DD') : value)
    },

    setTime (value) {
      this.$emit('update:time', value)
    },

    setDateTime (date, time) {
      this.setDate(date)
      this.setTime(time)
    }
  }
}
</script>
<style>
  .timeslot-picker__days {
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
  }
  .timeslot-picker__days > div {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .timeslot-picker__days .columns-2 {
    grid-column: 1 / span 2;
  }
  .timeslot-picker__days .timeslot-picker__date-list,
  .timeslot-picker__days .timeslot-picker__time-list {
    display: flex;
    flex-direction: column;
    background: #FFFFFF;
    border-radius: 4px;
    overflow: hidden;
  }
  .timeslot-date,
  .timeslot-time {
    transition: all .2s;
  }
  .timeslot-picker__days .timeslot-date,
  .timeslot-picker__days .timeslot-time,
  .timeslot-picker__days .timeslot-picker__future-dates {
    padding: 1rem 1.5rem;
    text-align: left;
    background: #FFFFFF;
    color: rgba(0, 0, 0, 0.6);
    appearance: none;
    cursor: pointer;
  }
  .timeslot-picker__days .timeslot-date:not(:last-child),
  .timeslot-picker__days .timeslot-time:not(:last-child) {
    border-bottom: 2px solid var(--theme-background);
  }
  .timeslot-picker__days .timeslot-date:hover,
  .timeslot-picker__days .timeslot-date[data-selected="true"],
  .timeslot-picker__days .timeslot-time:hover,
  .timeslot-picker__days .timeslot-time[data-selected="true"],
  .timeslot-picker__days .timeslot-picker__future-dates:hover {
    background: var(--theme-primary);
    color: #FFFFFF;
  }
  .timeslot-picker__week {
    border-radius: 4px;
    overflow: hidden;
  }
  .timeslot-picker__week .timeslot-picker__date-list > div {
    display: grid;
    grid-template-columns: 1fr 3fr;
  }
  .timeslot-picker__week .timeslot-date {
    display: flex;
    align-items: center;
  }
  .timeslot-picker__week .timeslot-picker__time-list {
    display: grid;
    grid-gap: 10px;
    grid-auto-columns: 1fr;
  }
  .timeslot-picker__week .timeslot-date,
  .timeslot-picker__week .timeslot-time {
    color: rgba(0, 0, 0, 0.6);
    appearance: none;
  }
  .timeslot-picker__week .timeslot-date {
    padding: 1rem 0;
    text-align: left;
  }
  .timeslot-picker__week .timeslot-time {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--theme-primary);
    border-radius: 6px;
    text-align: center;
    font-size: 0.8rem;
    padding: 0.1rem 0.2rem;
    min-height: 54px;
  }
  .timeslot-time:not(.timeslot-time__disabled) {
    background: #FFFFFF;
    cursor: pointer;
  }
  .timeslot-picker__week .timeslot-time:not(.timeslot-time__disabled):hover,
  .timeslot-picker__week .timeslot-time:not(.timeslot-time__disabled)[data-selected="true"] {
    background: var(--theme-primary);
    color: #FFFFFF;
  }
  .timeslot-picker__week .timeslot-time.timeslot-time__disabled {
    opacity: 0.5;
    filter: grayscale(1);
    background: transparent;
    cursor: not-allowed;
  }
  @media screen and (min-width: 769px) {
    .timeslot-picker__week .timeslot-date__row {
      margin-bottom: 0.5rem;
    }
    .timeslot-picker__week .timeslot-time {
      grid-row: 1 / auto;
    }
  }
  @media screen and (max-width: 768px) {
    .timeslot-picker__week .timeslot-picker__date-list > div {
      display: flex;
      flex-direction: column;
    }
    .timeslot-picker__week .timeslot-picker__time-list {
      grid-template-columns: repeat(2, 1fr);
      grid-auto-rows: 1fr;
    }
    .timeslot-picker__week .timeslot-time {
      min-height: unset;
      padding: 0.5rem 0.2rem;
    }
    .timeslot-picker__week .timeslot-date__row {
      border-bottom: 1px solid var(--theme-primary);
      padding-bottom: 1rem;
    }
  }
</style>
